import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  ArrowLeft,
  Send,
  Search,
  FileText,
  Users,
  Calendar as CalendarIcon,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  Flag
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../components/ui/utils";
import { Department } from "@/types";
import { getDepartmentCode } from "../utils/helpers";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";

interface PolicyMemoItem {
  id: string;
  title?: string;    // For policies
  subject?: string;  // For memos
  type: 'Policy' | 'Memo';
  status: 'Draft' | 'Active' | 'Archived';
  requireSignature: boolean;
  effectiveDate: string;
  version?: number;  // For policies
  number?: string;   // For memos
  description?: string;
}

export default function PolicyNewAssignPage() {
  const navigate = useNavigate();
  const { departments, policies, memos, refreshUserAssignments, refreshPolicies, refreshMemos, selectionUsers } = useData();
  const { currentUser } = useAuth();

  // Form state
  const [selectedItem, setSelectedItem] = useState<PolicyMemoItem | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string>("");
  const [priority, setPriority] = useState<string>("Medium");

  // Version tracking for conflict detection
  const [initialVersion, setInitialVersion] = useState<number | null>(null);
  const [versionConflict, setVersionConflict] = useState<{
    hasConflict: boolean;
    originalVersion: number;
    currentVersion: number;
  } | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("all");

  // Assignment mode: pick individual users or whole departments (memos only)
  const [assignmentMode, setAssignmentMode] = useState<'individual' | 'department'>('individual');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  useEffect(() => {
    refreshPolicies();
    refreshMemos();
  }, []);

  // Combine policies and memos for selection
  const availableItems = useMemo(() => {
    const policyItems: PolicyMemoItem[] = policies
      .filter(p => p.status === 'Active' && !p.deleted)
      .map(p => ({
        id: `policy-${p.id}`,
        title: p.title,
        type: 'Policy' as const,
        status: p.status,
        requireSignature: p.requireSignature,
        effectiveDate: p.effectiveDate,
        version: p.version,
        description: p.description
      }));

    const memoItems: PolicyMemoItem[] = memos
      .filter(m => m.status === 'Active' && !m.deleted)
      .map(m => ({
        id: `memo-${m.id}`,
        subject: m.subject,
        type: 'Memo' as const,
        status: m.status,
        requireSignature: m.requireSignature,
        effectiveDate: m.effectiveDate,
        number: m.number
      }));

    const allItems = [...policyItems, ...memoItems];

    if (typeFilter === "all") {
      return allItems; // If "all", return everything
    }

    // Otherwise, filter the list to only include items of the selected type
    return allItems.filter(item => item.type === typeFilter);

  }, [policies, memos, typeFilter]);

  // Filter users based on search and department
  // Uses selectionUsers (all active users) so managers can assign to anyone, not just direct reports
  const filteredUsers = useMemo(() => {
    return selectionUsers.filter(user => {
      if (user.archived || !user.isActive) return false;

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const nameMatch = user.name?.toLowerCase().includes(searchLower) ?? false;
        const emailMatch = user.email?.toLowerCase().includes(searchLower) ?? false;
        const jobRoleMatch = user.jobRole?.toLowerCase().includes(searchLower) ?? false;
        if (!nameMatch && !emailMatch && !jobRoleMatch) {
          return false;
        }
      }

      // Department filter
      if (departmentFilter !== "all") {
        const userDeptCode = typeof user.department === 'string' ? user.department : user.department?.departmentCode;
        if (userDeptCode !== departmentFilter) {
          return false;
        }
      }

      return true;
    });
  }, [selectionUsers, searchTerm, departmentFilter]);

  // Live member counts per department (primary department), shown in department mode
  const departmentUserCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    selectionUsers.forEach(user => {
      if (user.archived || !user.isActive) return;
      const code = typeof user.department === 'string' ? user.department : user.department?.departmentCode;
      if (code) counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [selectionUsers]);

  // Department mode is only supported for memos; reset when a policy is selected
  useEffect(() => {
    if (selectedItem?.type !== 'Memo' && assignmentMode === 'department') {
      setAssignmentMode('individual');
    }
  }, [selectedItem, assignmentMode]);

  const handleDepartmentToggle = (deptCode: string) => {
    setSelectedDepartments(prev =>
      prev.includes(deptCode)
        ? prev.filter(code => code !== deptCode)
        : [...prev, deptCode]
    );
  };

  // Track version when policy/memo changes
  useEffect(() => {
    if (selectedItem) {
      // Track initial version for conflict detection (policies only)
      if (selectedItem.type === 'Policy' && selectedItem.version) {
        console.log('[Policy Selection] Captured initial version:', selectedItem.version, typeof selectedItem.version);
        setInitialVersion(selectedItem.version);
        setVersionConflict(null); // Clear any existing conflict
      } else {
        setInitialVersion(null);
      }
    }
  }, [selectedItem]);

  // Handle user selection
  const handleUserToggle = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle select all users
  const handleSelectAll = () => {
    const currentUserIds = filteredUsers.map(u => u.id);
    const allSelected = currentUserIds.every(id => selectedUsers.includes(id));

    if (allSelected) {
      // Deselect all current users
      setSelectedUsers(prev => prev.filter(id => !currentUserIds.includes(id)));
    } else {
      // Select all current users
      setSelectedUsers(prev => [...new Set([...prev, ...currentUserIds])]);
    }
  };

  // Get department name
  const getDepartmentName = (deptCode: string | Department) => {
    const departmentCode = getDepartmentCode(deptCode);
    const dept = departments.find(d => d.departmentCode === departmentCode);
    return dept ? `${dept.departmentCode} - ${dept.departmentName}` : departmentCode;
  };

  // Get display name for policy/memo with truncation
  const getItemDisplayName = (item: PolicyMemoItem, maxLength: number = 64) => {
    const fullName = item.type === 'Policy' ? item.title : item.subject;
    if (!fullName) return '';
    return fullName.length > maxLength ? fullName.substring(0, maxLength) + '...' : fullName;
  };

  // Check for version conflicts before submission
  const checkVersionConflict = async (): Promise<boolean> => {
    if (!selectedItem || selectedItem.type !== 'Policy' || !initialVersion) {
      return false; // No conflict possible for memos or if no initial version
    }

    try {
      // Fetch current policy version from server
      // selectedItem.id is the composite key "policy-<id>"; strip the prefix for the API call
      const policyId = selectedItem.id.split('-')[1];
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${policyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const currentVersion = data.policy?.version;

        // Debug logging for submission check
        console.log('[Submission Check] Initial version:', initialVersion, typeof initialVersion);
        console.log('[Submission Check] Current version:', currentVersion, typeof currentVersion);

        // Convert both to numbers for comparison to avoid type mismatch
        const initialVersionNum = Number(initialVersion);
        const currentVersionNum = Number(currentVersion);

        if (currentVersionNum && currentVersionNum !== initialVersionNum) {
          console.log('[Submission Check] Conflict detected:', { initialVersionNum, currentVersionNum });
          setVersionConflict({
            hasConflict: true,
            originalVersion: initialVersionNum,
            currentVersion: currentVersionNum
          });
          return true;
        } else {
          console.log('[Submission Check] No conflict - versions match');
        }
      }
    } catch (error) {
      console.error('Error checking policy version:', error);
      // On error, proceed with submission (fail-safe approach)
    }

    return false;
  };

  // Validate form
  const isFormValid = () => {
    const hasAssignees = assignmentMode === 'department'
      ? selectedDepartments.length > 0
      : selectedUsers.length > 0;
    return selectedItem && hasAssignees && dueDate && !versionConflict?.hasConflict;
  };

  // Handle version conflict resolution
  const handleVersionConflict = async (action: 'use-latest' | 'cancel') => {
    if (!versionConflict || !selectedItem) return;

    if (action === 'use-latest') {
      // Fetch latest version and update selected item
      try {
        const policyId = selectedItem.id.split('-')[1];
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}/api/policies/${policyId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const latestPolicy = data.policy;
          setSelectedItem({
            ...selectedItem,
            version: latestPolicy.version,
            title: latestPolicy.title,
            description: latestPolicy.description,
            requireSignature: latestPolicy.requireSignature,
            effectiveDate: latestPolicy.effectiveDate
          });
          setInitialVersion(latestPolicy.version);
          setVersionConflict(null);
          toast.success('Updated to use latest policy version');
        }
      } catch (error) {
        toast.error('Failed to fetch latest policy version');
      }
    } else {
      // Cancel - clear selection
      setSelectedItem(null);
      setInitialVersion(null);
      setVersionConflict(null);
      toast.info('Policy selection cleared');
    }
  };

  // Submit assignment
  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    // Check for version conflicts before submission
    const hasConflict = await checkVersionConflict();
    if (hasConflict) {
      setIsSubmitting(false);
      return; // Don't submit, show conflict resolution UI
    }

    try {
      const endpoint = selectedItem!.type === 'Policy'
        ? '/api/policies/assignments'
        : '/api/memos/assignments';

      const isDepartmentAssignment = assignmentMode === 'department' && selectedItem!.type === 'Memo';

      const payload = {
        [selectedItem!.type === 'Policy' ? 'policyId' : 'memoId']: selectedItem!.id.split('-')[1],
        userIds: selectedUsers,
        ...(isDepartmentAssignment ? { departmentCodes: selectedDepartments } : {}),
        assignmentType: isDepartmentAssignment ? "Department" : "Individual",
        assignmentPriority: priority,
        dueDate: new Date(dueDate).toISOString(),
        requireSignature: selectedItem!.requireSignature
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || "http://localhost:5001"}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Assignment created successfully');

        // Refresh user assignments to update My Policies page
        await refreshUserAssignments();

        navigate('/policies?tab=assignments');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create assignment');
      }
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Can I={ACTIONS.CREATE} a={RESOURCES.POLICY_ASSIGNMENT} passThrough={
      <div className="p-4 md:p-6">
        <Card>
          <CardContent className="p-6 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="mb-2">Access Restricted</h3>
            <p className="text-muted-foreground">You don't have permission to view policies.</p>
          </CardContent>
        </Card>
      </div>
    }>
      <div className="space-y-6 p-2 md:p-0">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-4">
            <Link to="/policies?tab=assignments">
              <Button variant="outline" size="icon" className="flex-shrink-0">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">New Assignment</h1>
              <p className="text-sm text-muted-foreground">Assign a policy or memo to users</p>
            </div>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || isSubmitting}
            className="w-full md:w-auto"
          >
            <Send className="h-4 w-4 mr-2" />
            {isSubmitting ? 'Publishing...' : 'Publish Assignment'}
          </Button>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Version Conflict Alert */}
            {versionConflict?.hasConflict && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Policy Version Conflict</AlertTitle>
                <AlertDescription className="space-y-3">
                  <p>This policy was updated to v{versionConflict.currentVersion}.</p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => handleVersionConflict('use-latest')}>Use Latest Version</Button>
                    <Button size="sm" variant="outline" onClick={() => handleVersionConflict('cancel')}>Cancel</Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Policy/Memo Selection */}
            <Card>
              <CardHeader>
                <CardTitle>1. Select Policy or Memo</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-grow space-y-2">
                    <Label>Policy or Memo *</Label>
                    <Select value={selectedItem?.id || ""} onValueChange={(value) => setSelectedItem(availableItems.find(i => i.id === value) || null)}>
                      <SelectTrigger><SelectValue placeholder="Select an item..." /></SelectTrigger>
                      <SelectContent>
                        {availableItems.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            <div className="flex items-center gap-2">
                              <Badge variant={item.type === 'Policy' ? 'default' : 'secondary'}>{item.type}</Badge>
                              <span className="block md:hidden">{getItemDisplayName(item, 30)}</span>
                              <span className="hidden md:block">{getItemDisplayName(item, 40)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Filter by Type</Label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="w-full sm:w-[120px]"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        <SelectItem value="Policy">Policy</SelectItem>
                        <SelectItem value="Memo">Memo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {selectedItem && (
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg text-sm">
                    <p className="font-medium">{getItemDisplayName(selectedItem)}</p>
                    <p className="text-xs text-muted-foreground">{selectedItem.description || 'No description available.'}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Assignment Details */}
            {selectedItem && (
              <Card>
                <CardHeader><CardTitle>2. Assignment Details</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="dueDate">Due Date *</Label>
                    <Input id="dueDate" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority *</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                        <SelectItem value="Critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* User/Department Selection */}
            {selectedItem && (
              <Card>
                <CardHeader><CardTitle>3. Assign {assignmentMode === 'department' ? 'Departments' : 'Users'}</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {selectedItem.type === 'Memo' && (
                    <Tabs value={assignmentMode} onValueChange={(v) => setAssignmentMode(v as 'individual' | 'department')}>
                      <TabsList>
                        <TabsTrigger value="individual">By User</TabsTrigger>
                        <TabsTrigger value="department">By Department</TabsTrigger>
                      </TabsList>
                    </Tabs>
                  )}

                  {assignmentMode === 'individual' ? (
                    <>
                      <div className="flex flex-col sm:flex-row gap-4">
                        <Input placeholder="Search users..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="flex-grow" />
                        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                          <SelectTrigger className="w-full sm:w-auto"><SelectValue placeholder="All Departments" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Departments</SelectItem>
                            {departments.filter(d => !d.archived).map((dept) => (<SelectItem key={dept.id} value={dept.departmentCode}>{dept.departmentName}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-muted-foreground">{selectedUsers.length} user(s) selected</p>
                        <Button variant="outline" size="sm" onClick={handleSelectAll}>{filteredUsers.every(u => selectedUsers.includes(u.id)) ? 'Deselect All' : 'Select All'}</Button>
                      </div>
                      <div className="border rounded-lg max-h-[400px] overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-12"><Checkbox checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedUsers.includes(u.id))} onCheckedChange={handleSelectAll} /></TableHead>
                              <TableHead>User</TableHead>
                              <TableHead className="hidden sm:table-cell">Department</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredUsers.map((user) => (
                              <TableRow key={user.id}>
                                <TableCell><Checkbox checked={selectedUsers.includes(user.id)} onCheckedChange={() => handleUserToggle(user.id)} /></TableCell>
                                <TableCell>
                                  <p className="font-medium">{user.name}</p>
                                  <p className="text-sm text-muted-foreground sm:hidden">{getDepartmentName(user.department)}</p>
                                </TableCell>
                                <TableCell className="hidden sm:table-cell">{getDepartmentName(user.department)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {filteredUsers.length === 0 && <p className="text-center py-8 text-muted-foreground">No users found.</p>}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground">
                        The memo will be assigned to all current members of the selected departments.
                      </p>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-muted-foreground">{selectedDepartments.length} department(s) selected</p>
                      </div>
                      <div className="border rounded-lg max-h-[400px] overflow-y-auto divide-y">
                        {departments.filter(d => !d.archived).map((dept) => (
                          <label key={dept.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50">
                            <Checkbox
                              checked={selectedDepartments.includes(dept.departmentCode)}
                              onCheckedChange={() => handleDepartmentToggle(dept.departmentCode)}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{dept.departmentName}</p>
                              <p className="text-xs text-muted-foreground">{dept.departmentCode}</p>
                            </div>
                            <Badge variant="secondary">{departmentUserCounts[dept.departmentCode] || 0} user(s)</Badge>
                          </label>
                        ))}
                        {departments.filter(d => !d.archived).length === 0 && (
                          <p className="text-center py-8 text-muted-foreground">No departments found.</p>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar Column */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader><CardTitle>Assignment Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div>
                  <p className="font-medium text-muted-foreground">Item</p>
                  <p>{selectedItem ? getItemDisplayName(selectedItem, 35) : "None"}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Due Date</p>
                  <p>{dueDate ? new Date(dueDate).toLocaleDateString() : "Not set"}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">Priority</p>
                  <p>{priority}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground">{assignmentMode === 'department' ? 'Departments' : 'Users'}</p>
                  <p>
                    {assignmentMode === 'department'
                      ? `${selectedDepartments.length} selected`
                      : `${selectedUsers.length} selected`}
                  </p>
                </div>
                <div className="pt-4 border-t">
                  <p className="text-xs text-muted-foreground mb-2">Required to Publish *</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2"><CheckCircle className={cn("h-3 w-3", selectedItem ? "text-green-600" : "text-muted-foreground")} /><span>Item selected</span></div>
                    <div className="flex items-center gap-2"><CheckCircle className={cn("h-3 w-3", dueDate ? "text-green-600" : "text-muted-foreground")} /><span>Due date set</span></div>
                    <div className="flex items-center gap-2"><CheckCircle className={cn("h-3 w-3", (assignmentMode === 'department' ? selectedDepartments.length > 0 : selectedUsers.length > 0) ? "text-green-600" : "text-muted-foreground")} /><span>{assignmentMode === 'department' ? 'Departments selected' : 'Users selected'}</span></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Can>
  );
}
